import { Schema, normalize, schema } from "normalizr";
import { call, fork, put, take, actionChannel } from "redux-saga/effects";
import type { Channel } from "@redux-saga/core";
import type { ActionPattern } from "@redux-saga/types";
import { ReducerMethods } from "../store/reducer.methods";
import BaseContext from "../context/base-context";
import IContextContainer from "../context/icontext-container";
import { entitiesReducer } from "../store/entities/entities.reducer";
import { paginationsReducer } from "../store/paginations/paginations.reducer";
import { SagaEffectAction } from "../types/store.types";
import { IReducer } from "../decorators/reducer.decorator";
import { PagerReducer } from "../decorators/pager.decorator";
import 'reflect-metadata';
import { LoaderReducer } from "../decorators/loader.decorator";
import { loadersReducer } from "../store/loaders/loaders.reducer";
import { Loaders } from "../store/loaders/loaders.enum";
import { LoadersMethods } from "../store/loaders/loaders.methods";
import { FlaggerReducer } from "../decorators/flagger.decorator";
import { flaggersReducer } from "../store/flaggers/flaggers.reducer";
import { Flaggers } from "../store/flaggers/flaggers.enum";
import { FlaggersMethods } from "../store/flaggers/flaggers.methods";
import { Entities } from "../store/entities/entities.enum";
import { ROUTE_PATHS } from "../../constants/app.constants";

export interface HttpRequestConfig<ReqBody extends Object> {
  url: string;
  body?: ReqBody;
  headers?: HeadersInit;
  isFullUrl?: boolean;
  ignoreMsg?: boolean;
  params?: Record<string, any>;
  domain?: string;
  omitAuth?: boolean;
}

export interface ISagaMethod {
  className: string;
  methodName: string;
}

export enum ToastifyEffectActions {
  ADD_TOASTIFY = 'toastifies_addToastify',
}

export interface InitSchemaOpts {
  name: string,
  definitions: Schema,
  options: schema.EntityOptions,
}

interface IAction {
  type: string; 
  payload: Object;
}

function sagaAction(
  type: string,
  payload: Record<string, any> = {},
): SagaEffectAction<Object> {
  return { type, payload};
}

const MESSAGE = 'message';
const PAGER = 'pager';
const ENTITIES = 'data';

export class BaseService extends BaseContext {
  private _idAttribute: string = '';
  private _schema: schema.Entity[] = [];
  private _entityName: string = '';
  private _actions: { [key: string]: Object } = {};

  constructor(ctx: IContextContainer) {
    super(ctx);
  }

  public static sagas(di: IContextContainer) {
    const objects: ISagaMethod[] = Reflect.getMetadata('sagas', BaseService) || [];
    return objects.map(obj => {
      try {
        const classInstance = di[obj.className as keyof typeof di] as unknown as BaseService;
        const actionName = classInstance.entityName + '_' + obj.methodName;
        const method = (classInstance[obj.methodName as keyof typeof classInstance] as any).bind(classInstance);
        classInstance._actions[obj.methodName] = (data?: any) =>
          sagaAction(actionName, data);
        const saga = function* (): Generator {
          const channel: unknown = yield actionChannel(actionName);
          while (true) {
            try {
              const data: unknown = yield take(channel as any);
              yield call(method, (data as any).payload);
            } catch (error) {
              const url = new URL(window.location.href);
              url.searchParams.set('error', 'true');
              window.location.href = url.toString();
            }
          }
        };
        return fork(saga);
      } catch (error) {
        const saga = function* () {};
        return fork(saga);
      }
    });
  }

  public static reducers(di: IContextContainer) {
    const entities: IReducer[] = Reflect.getMetadata('entities', BaseService) || [];
    const entityReducers = entities.reduce((result, reducerOpts) => {
      if (reducerOpts.initSchema) {
        try {
          const classInstance = di[reducerOpts.className as keyof typeof di] as unknown as BaseService;
          classInstance.initSchema({ ...reducerOpts.initSchema, name: reducerOpts.entityName });
        } catch (error) { }
      }
      const getReducer = () => {
          if (reducerOpts.reducerFunc) return reducerOpts.reducerFunc;
          return (state: any, action: any) => entitiesReducer(state, action, reducerOpts.entityName);
      } 
      result[reducerOpts.entityName] = getReducer();
      return result;
    }, {} as any);

    const pagers: PagerReducer[] = Reflect.getMetadata('pagers', BaseService) || [];
    const pagerReducers = pagers.reduce((result, pagerOpts) => {
      result[pagerOpts.pagerName] = 
          (state: any, action: any) => paginationsReducer(state, action, pagerOpts.pagerName);
      return result;
    }, entityReducers);

    const loaders: LoaderReducer[] = Reflect.getMetadata('loaders', BaseService) || [];
    const loaderReducers = loaders.reduce((result, loaderOpts) => {
      result[loaderOpts.loaderName] = 
          (state: any, action: any) => loadersReducer(state, action, loaderOpts.loaderName);
      return result;
    }, pagerReducers);

    const flaggers: FlaggerReducer[] = Reflect.getMetadata('flaggers', BaseService) || [];
    const flaggerReducers = flaggers.reduce((result, flaggerOpts) => {
      result[flaggerOpts.flaggerName] = 
          (state: any, action: any) => flaggersReducer(state, action, flaggerOpts.flaggerName);
      return result;
    }, loaderReducers);
    return flaggerReducers;
  } 

  private async sendRequest<ReqBody extends Object, ResBody extends Object>
  (url: string, method: string = 'GET', body: ReqBody | null = null, headers: HeadersInit = {}): Promise<ResBody | Error> {
    try {
      const resHeaders: HeadersInit = body instanceof FormData ? headers : {
        'Content-Type': 'application/json',
        ...headers,
      };
      const response = await fetch(url, { method, body: this.getBody(method, body), headers: resHeaders });
      
      if (response.status === 401) {
        window.localStorage.clear();
        const url = new URL(window.location.href);
        url.searchParams.set('logout', 'true');
        window.location.href = url.toString();
        return new Error('Unauthorized - Session expired');
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'Request error');
      return data;
    } catch (error) {
      return error as Error;
    }
  }

  private getBody(method: string, body: any) {
    if (method === 'GET') return null;
    if (body instanceof FormData) return body;
    return JSON.stringify(body);
  }

  private normalizeData(data: any) {
    return normalize(Array.isArray(data) ? data : [data], this.entitySchema);
  }

  private getPager(data: any) {
    const pageName = data.pager.paginationName;
    const entitiesData = data.data;
    const normalizedEntities: any = this.normalizeData(entitiesData);
    const page = {
      ...data.pager,
      pageIds: normalizedEntities.result.length > 0 
        ? Object.keys(normalizedEntities.entities[this.entityName])
        : []
    }
    const action: IAction = {
      type: ReducerMethods.UPDATE,
      payload: { 
        entities: {
          ...normalizedEntities.entities,
          [pageName]: page 
        } 
      }
    }
    return action;
  }

  protected initSchema(opts: InitSchemaOpts) {
    this._entityName = opts.name;
    this._schema =
      opts.name && opts.name !== 'entity'
        ? [new schema.Entity(opts.name, opts.definitions, opts.options)]
        : [];
    this._idAttribute = (opts.options?.idAttribute || '') as string;
    this.sendRequest = this.sendRequest.bind(this);
  }

  protected *requestResult<ReqBody extends Object, ResBody extends Object>
  (config: HttpRequestConfig<ReqBody>, httpMethod: string, actionMethod: string) {
    let data: object | Error = yield call(
      this.sendRequest<ReqBody, ResBody>, 
      config.url, 
      httpMethod, 
      config.body,
      config.headers
    );
    if (this._idAttribute) {
      let currentData: any = data;
      const action = this.normalizeReqBody(currentData, actionMethod);
      yield put(action);
    }
    return data as ResBody;
  }

  public get entityName(): string {
    return this._entityName;
  }

  public get entitySchema(): schema.Entity[] {
    return this._schema;
  }

  public normalizeReqBody(
      data: any, 
      actionMethod: string
  ): IAction {
    if (data.hasOwnProperty(PAGER)) {
      return this.getPager(data);
    }
    if (data.hasOwnProperty(ENTITIES)) {
      data = data.data;
    }
    return {
      type: actionMethod,
      payload: this.normalizeData(data)
    };
  }

  protected *setLoaderState(loader: Loaders, isLoading: boolean) {
    yield put({
      type: LoadersMethods.UpdateLoader,
      payload: { entities: { [loader]: isLoading } },
    });
  }

  protected *setFlaggerState<T extends Object>(flagger: Flaggers, data: T | null) {
    yield put({
      type: FlaggersMethods.UpdateFlagger,
      payload: { entities: { [flagger]: data } },
    });
  }

  protected *setEntityStateById<T extends Object>(entity: Entities, id: string | number, data: T) {
    yield put({
      type: ReducerMethods.UPDATE,
      payload: { entities: { [entity]: { [id]: data } } },
    });
  }

  protected *setEntityState<T extends Object>(entity: Entities, data: T) {
    yield put({
      type: ReducerMethods.UPDATE,
      payload: { entities: { [entity]: data } },
    });
  }
}